/**
 * sampleVault — load the committed fixture vault as ParsedNote[] / ParsedFlow[].
 *
 * Uses Vite's `import.meta.glob` so the sample is bundled at build time
 * with no runtime fetch. Only the fixture vault is shipped this way;
 * live mode reads via Graph (C.9) and demo mode reads from Convex (C.16).
 */

import { parseFlowFile, parseVaultNote, type ParsedFlow, type ParsedNote } from './parse';

// Vite glob paths must start with `./` or `../`, relative to THIS file.
// This file lives at src/lib/vault/sampleVault.ts, so the fixture root
// is three directories up.
const noteFiles = import.meta.glob<string>(
  '../../../fixtures/vaults/areweai-sample/**/*.md',
  { eager: true, query: '?raw', import: 'default' },
);
const flowFiles = import.meta.glob<string>(
  '../../../fixtures/vaults/areweai-sample/**/*.flow',
  { eager: true, query: '?raw', import: 'default' },
);

function relativize(absPath: string): string {
  const marker = 'fixtures/vaults/areweai-sample/';
  const idx = absPath.indexOf(marker);
  return idx >= 0 ? absPath.slice(idx + marker.length) : absPath;
}

export interface SampleVault {
  notes: ParsedNote[];
  flows: ParsedFlow[];
}

export function loadSampleVault(): SampleVault {
  const notes: ParsedNote[] = [];
  for (const [abs, raw] of Object.entries(noteFiles)) {
    notes.push(parseVaultNote(relativize(abs), raw));
  }
  notes.sort((a, b) => a.path.localeCompare(b.path));
  const flows: ParsedFlow[] = [];
  for (const [abs, raw] of Object.entries(flowFiles)) {
    try {
      flows.push(parseFlowFile(relativize(abs), raw));
    } catch {
      // ignore malformed fixtures so dev never gets stuck
    }
  }
  flows.sort((a, b) => a.path.localeCompare(b.path));
  return { notes, flows };
}
